import React, { createContext, useCallback, useContext, useEffect, useRef } from "react"
import { InteractionManager } from "react-native"

import {
  generateThumbnail,
  getCachedThumbnail,
  setCachedThumbnail,
} from "src/hooks/useVideoThumbnail"

/**
 * Request for thumbnail generation with priority ordering.
 * Higher priority values are processed first (most recent messages should have higher priority).
 */
interface ThumbnailRequest {
  videoUrl: string
  priority: number
  resolve: (uri: string | undefined) => void
  cancelled: boolean
}

interface ThumbnailQueueContextValue {
  /**
   * Queue a thumbnail for generation with a given priority.
   * Higher priority values are processed first.
   * Returns a promise that resolves to the thumbnail URI or undefined on failure.
   *
   * @param videoUrl - The video URL to generate a thumbnail for
   * @param priority - Priority value (higher = processed first, use Date.now() for recency)
   * @returns Promise resolving to thumbnail URI or undefined
   */
  queueThumbnail: (videoUrl: string, priority: number) => Promise<string | undefined>

  /**
   * Cancel a pending thumbnail request for a given video URL.
   * Used when a component unmounts before its thumbnail is processed.
   */
  cancelThumbnail: (videoUrl: string) => void
}

const ThumbnailQueueContext = createContext<ThumbnailQueueContextValue | null>(null)

/**
 * Provider for the thumbnail generation queue.
 * Ensures thumbnails are generated one at a time to avoid resource contention
 * that could block the microphone or UI.
 */
export function ThumbnailQueueProvider({ children }: { children: React.ReactNode }) {
  // Queue of pending thumbnail requests, sorted by priority (highest first)
  const queueRef = useRef<ThumbnailRequest[]>([])
  // Track if we're currently processing a thumbnail
  const isProcessingRef = useRef(false)
  // Track active requests by URL for cancellation
  const activeRequestsRef = useRef<Map<string, ThumbnailRequest>>(new Map())

  /**
   * Process the next item in the queue.
   * Uses InteractionManager to wait for UI to be idle before processing.
   */
  const processQueue = useCallback(() => {
    // If already processing or queue is empty, return
    if (isProcessingRef.current || queueRef.current.length === 0) {
      return
    }

    isProcessingRef.current = true

    // Use InteractionManager to wait for UI interactions to complete
    // This ensures thumbnail generation doesn't block UI animations or microphone
    const handle = InteractionManager.runAfterInteractions(async () => {
      // Get the highest priority request (first in sorted queue)
      const request = queueRef.current.shift()

      if (!request) {
        isProcessingRef.current = false
        return
      }

      // Remove from active requests map
      activeRequestsRef.current.delete(request.videoUrl)

      // Skip if cancelled
      if (request.cancelled) {
        console.debug("[ThumbnailQueue] Skipping cancelled request for:", request.videoUrl)
        isProcessingRef.current = false
        // Continue processing queue
        processQueue()
        return
      }

      try {
        // Check cache first (might have been cached by another request)
        const cached = getCachedThumbnail(request.videoUrl)
        if (cached) {
          console.debug("[ThumbnailQueue] Using cached thumbnail:", request.videoUrl)
          request.resolve(cached)
          isProcessingRef.current = false
          processQueue()
          return
        }

        console.debug(
          "[ThumbnailQueue] Generating thumbnail for:",
          request.videoUrl,
          "Priority:",
          request.priority,
          "Queue length:",
          queueRef.current.length,
        )

        // Generate the thumbnail
        const uri = await generateThumbnail(request.videoUrl)

        // Cache it
        setCachedThumbnail(request.videoUrl, uri)

        // Resolve if not cancelled during generation
        if (!request.cancelled) {
          request.resolve(uri)
        }
      } catch (error) {
        console.warn("[ThumbnailQueue] Failed to generate thumbnail:", request.videoUrl, error)
        // Resolve with undefined on error (don't reject to avoid unhandled promise rejections)
        if (!request.cancelled) {
          request.resolve(undefined)
        }
      } finally {
        isProcessingRef.current = false
        // Continue processing queue
        processQueue()
      }
    })

    // Clean up if component unmounts during processing
    return () => {
      handle.cancel()
    }
  }, [])

  /**
   * Queue a thumbnail for generation.
   * If already cached, returns immediately.
   * Otherwise, adds to priority queue and processes when ready.
   */
  const queueThumbnail = useCallback(
    (videoUrl: string, priority: number): Promise<string | undefined> => {
      // Check cache first - return immediately if cached
      const cached = getCachedThumbnail(videoUrl)
      if (cached) {
        return Promise.resolve(cached)
      }

      // Check if already in queue - return existing promise
      const existing = activeRequestsRef.current.get(videoUrl)
      if (existing && !existing.cancelled) {
        // Update priority if new request has higher priority
        if (priority > existing.priority) {
          existing.priority = priority
          // Re-sort queue
          queueRef.current.sort((a, b) => b.priority - a.priority)
        }
        return new Promise((resolve) => {
          // Chain to existing request
          const originalResolve = existing.resolve
          existing.resolve = (uri) => {
            originalResolve(uri)
            resolve(uri)
          }
        })
      }

      // Create new request
      return new Promise((resolve) => {
        const request: ThumbnailRequest = {
          videoUrl,
          priority,
          resolve,
          cancelled: false,
        }

        // Add to active requests map
        activeRequestsRef.current.set(videoUrl, request)

        // Add to queue and sort by priority (highest first)
        queueRef.current.push(request)
        queueRef.current.sort((a, b) => b.priority - a.priority)

        console.debug(
          "[ThumbnailQueue] Queued thumbnail:",
          videoUrl,
          "Priority:",
          priority,
          "Queue length:",
          queueRef.current.length,
        )

        // Start processing if not already
        processQueue()
      })
    },
    [processQueue],
  )

  /**
   * Cancel a pending thumbnail request.
   */
  const cancelThumbnail = useCallback((videoUrl: string) => {
    const request = activeRequestsRef.current.get(videoUrl)
    if (request) {
      request.cancelled = true
      activeRequestsRef.current.delete(videoUrl)
      console.debug("[ThumbnailQueue] Cancelled thumbnail request:", videoUrl)
    }
  }, [])

  const value: ThumbnailQueueContextValue = {
    queueThumbnail,
    cancelThumbnail,
  }

  return <ThumbnailQueueContext.Provider value={value}>{children}</ThumbnailQueueContext.Provider>
}

/**
 * Hook to access the thumbnail queue context.
 * Throws if used outside of ThumbnailQueueProvider.
 */
export function useThumbnailQueue(): ThumbnailQueueContextValue {
  const context = useContext(ThumbnailQueueContext)
  if (!context) {
    throw new Error("useThumbnailQueue must be used within a ThumbnailQueueProvider")
  }
  return context
}

// Hook result interface matching useVideoThumbnail
export interface UseQueuedVideoThumbnailResult {
  thumbnailUri: string | undefined
  isLoading: boolean
  isError: boolean
  error: Error | undefined
}

/**
 * Hook to load video thumbnails using the priority queue.
 * This ensures thumbnails are generated one at a time to avoid blocking
 * the microphone and UI.
 *
 * @param videoUrl - The URL of the video to generate a thumbnail for
 * @param priority - Priority value (higher = processed first). Use Date.now() for recency-based ordering.
 * @param existingThumbnailUrl - Optional. When set (e.g. from backend), skips generation and returns this as thumbnailUri.
 * @returns Object containing thumbnailUri, isLoading, isError, and error
 *
 * @example
 * const { thumbnailUri, isLoading } = useQueuedVideoThumbnail(videoUrl, messageTimestamp)
 * @example
 * const { thumbnailUri } = useQueuedVideoThumbnail(videoUrl, priority, backendThumbnailUrl)
 */
export function useQueuedVideoThumbnail(
  videoUrl: string,
  priority: number = 0,
  existingThumbnailUrl?: string,
): UseQueuedVideoThumbnailResult {
  const [thumbnailUri, setThumbnailUri] = React.useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = React.useState(!!videoUrl && !existingThumbnailUrl)
  const [isError, setIsError] = React.useState(false)
  const [error, setError] = React.useState<Error | undefined>(undefined)

  const { queueThumbnail, cancelThumbnail } = useThumbnailQueue()

  useEffect(() => {
    if (existingThumbnailUrl) {
      setIsLoading(false)
      return
    }
    if (!videoUrl) {
      setIsLoading(false)
      return
    }
    let cancelled = false

    async function loadThumbnail() {
      // Reset state when URL changes
      setIsLoading(true)
      setIsError(false)
      setError(undefined)

      try {
        const uri = await queueThumbnail(videoUrl, priority)

        if (!cancelled) {
          if (uri) {
            setThumbnailUri(uri)
          } else {
            // No URI means generation failed
            setIsError(true)
            setError(new Error("Thumbnail generation failed"))
          }
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setIsError(true)
          setError(err instanceof Error ? err : new Error(String(err)))
          setIsLoading(false)
        }
      }
    }

    loadThumbnail()

    // Cleanup: cancel request and mark as cancelled
    return () => {
      cancelled = true
      cancelThumbnail(videoUrl)
    }
  }, [videoUrl, priority, existingThumbnailUrl, queueThumbnail, cancelThumbnail])

  return {
    thumbnailUri: existingThumbnailUrl ?? thumbnailUri,
    isLoading,
    isError,
    error,
  }
}
